<script lang="ts">
  import {createEventDispatcher} from 'svelte';
  import {navigateTo, router} from 'yrv';
  import InlineSVG from 'svelte-inline-svg';

  import {NEW_PROJECT} from '../../constants/views';

  import {ProjectType} from '../../types/project';

  import {user} from '../../stores/user';
  import {
    searchText,
    typeFilter,
    confidentialityFilter,
    visibilityFilter,
    diamondFilter,
    customersFilter,
    teamFilter,
    fieldsFilter,
    topicsFilter,
    servicesFilter,
    startDateFilter,
    endDateFilter,
    showDrafts,
    tasksFilter,
    technologiesFilter,
    formatsFilter,
    methodsFilter,
    minValueInTimeFilter,
    maxValueInTimeFilter,
    minValueInPriceFilter,
    maxValueInPriceFilter
  } from '../../stores/filter-and-sort';

  import filterIcon from '../../assets/icons/filter.svg';
  import addIcon from '../../assets/icons/add.svg';

  import IconButton from '../icon-button/IconButton.svelte';
  import LinkButton from '../link-button/LinkButton.svelte';
  import FilterPanel from '../filter-panel/FilterPanel.svelte';
  import Chip from '../chip/Chip.svelte';
  import SortButton from '../sort-button/SortButton.svelte';
  import Expandable from '../expandable/Expandable.svelte';

  const dispatch = createEventDispatcher();

  export let numberOfProjectsTotal: number;
  export let numberOfProjectsFiltered: number;

  let showFiltersPanel = false;

  $: filtersAvailable = Boolean(
    $typeFilter ||
      $confidentialityFilter ||
      $visibilityFilter ||
      $startDateFilter ||
      $endDateFilter ||
      $customersFilter.length ||
      $teamFilter.length ||
      $diamondFilter.length ||
      $fieldsFilter.length ||
      $topicsFilter.length ||
      $servicesFilter.length ||
      $showDrafts === false ||
      $tasksFilter.length ||
      $technologiesFilter.length ||
      $formatsFilter.length ||
      $methodsFilter.length ||
      $minValueInTimeFilter != null ||
      $maxValueInTimeFilter != null ||
      $minValueInPriceFilter != null ||
      $maxValueInPriceFilter != null
  );
  $: filtersOrSearchTextAvailable = Boolean(filtersAvailable || $searchText);
</script>

<style lang="postcss">
  .listHeader {
    position: relative;
    display: flex;
    align-items: center;
    grid-gap: 0.5rem;
  }

  :global(button.iconButtonActive::before) {
    opacity: 0.2;
  }

  :global(.addProjectButton) {
    margin-left: auto;
  }

  :global(.filters) {
    z-index: 11;
  }

  .stickyHeader {
    z-index: 10;
    position: sticky;
    top: 6.25rem;
    display: grid;
    grid-gap: 0.5rem;
    margin: 0 -0.5rem;
    padding: 0.5rem;
    background-color: var(--ubilabs-white);
  }

  @media (min-width: 640px) {
    .stickyHeader {
      top: 3.5rem;
      margin: 0 -1rem;
      padding: 0.75rem 1rem;
    }
  }

  @media (min-width: 768px) {
    .stickyHeader {
      top: 4.5rem;
      margin: 0 -1.5rem;
      padding: 0.75rem 1.5rem;
    }
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -0.25rem;
  }

  .numberOfProjects {
    margin-bottom: 0.25rem;
    color: var(--ubilabs-gray-dark-2);
  }
</style>

<div class="listHeader">
  <IconButton
    class={showFiltersPanel ? 'iconButtonActive' : ''}
    title="Filter projects"
    aria-label="Filter projects"
    on:click={() => (showFiltersPanel = !showFiltersPanel)}>
    <InlineSVG src={filterIcon} />
  </IconButton>

  <SortButton />

  {#if $user}
    <LinkButton
      class="addProjectButton"
      on:click={() => navigateTo(NEW_PROJECT, {params: $router.params})}
      aria-labelledby="add-project-label">
      <span slot="icon"><InlineSVG src={addIcon} /></span>
      <span id="add-project-label">Add project</span>
    </LinkButton>
  {/if}
</div>

<Expandable class="filters" bind:expanded={showFiltersPanel}>
  <FilterPanel
    {numberOfProjectsFiltered}
    {numberOfProjectsTotal}
    on:resetFilters />
</Expandable>

<div class="stickyHeader">
  {#if filtersOrSearchTextAvailable}
    <div class="chips">
      <!-- General filters -->
      {#if $searchText}
        <Chip onRemove={() => searchText.reset()}>
          Search: {$searchText}
        </Chip>
      {/if}

      {#if $typeFilter}
        <Chip
          color={$typeFilter === ProjectType.CONSULTING ? 'yellow' : 'green'}
          onRemove={() => ($typeFilter = null)}>
          {$typeFilter === ProjectType.CONSULTING
            ? 'Only consulting projects'
            : 'Only development projects'}
        </Chip>
      {/if}

      {#if $confidentialityFilter}
        <Chip onRemove={() => ($confidentialityFilter = null)}>
          {$confidentialityFilter}
        </Chip>
      {/if}

      {#if $visibilityFilter}
        <Chip onRemove={() => ($visibilityFilter = null)}>
          {$visibilityFilter}
        </Chip>
      {/if}

      {#if $startDateFilter}
        <Chip onRemove={() => ($startDateFilter = null)}>
          Created after: {$startDateFilter.toLocaleDateString()}
        </Chip>
      {/if}

      {#if $endDateFilter}
        <Chip onRemove={() => ($endDateFilter = null)}>
          Created before: {$endDateFilter.toLocaleDateString()}
        </Chip>
      {/if}

      {#if $customersFilter}
        {#each $customersFilter as customerFilter}
          <Chip
            onRemove={() =>
              ($customersFilter = $customersFilter.filter(
                customer => customer !== customerFilter
              ))}>
            Customer: {customerFilter}
          </Chip>
        {/each}
      {/if}

      {#if $teamFilter}
        {#each $teamFilter as teamMemberFilter}
          <Chip
            onRemove={() =>
              ($teamFilter = $teamFilter.filter(
                teamMember => teamMember !== teamMemberFilter
              ))}>
            Team: {teamMemberFilter}
          </Chip>
        {/each}
      {/if}

      {#if $diamondFilter}
        {#each $diamondFilter as diamondSectionFilter}
          <Chip
            onRemove={() =>
              ($diamondFilter = $diamondFilter.filter(
                diamondSection => diamondSection !== diamondSectionFilter
              ))}>
            Diamond: {diamondSectionFilter}
          </Chip>
        {/each}
      {/if}

      {#if $fieldsFilter}
        {#each $fieldsFilter as fieldFilter}
          <Chip
            onRemove={() =>
              ($fieldsFilter = $fieldsFilter.filter(
                field => field !== fieldFilter
              ))}>
            Field: {fieldFilter}
          </Chip>
        {/each}
      {/if}

      {#if $topicsFilter}
        {#each $topicsFilter as topicFilter}
          <Chip
            onRemove={() =>
              ($topicsFilter = $topicsFilter.filter(
                topic => topic !== topicFilter
              ))}>
            Topic: {topicFilter}
          </Chip>
        {/each}
      {/if}

      {#if $servicesFilter}
        {#each $servicesFilter as serviceFilter}
          <Chip
            onRemove={() =>
              ($servicesFilter = $servicesFilter.filter(
                service => service !== serviceFilter
              ))}>
            Service: {serviceFilter}
          </Chip>
        {/each}
      {/if}

      {#if !$showDrafts}
        <Chip onRemove={() => ($showDrafts = true)}>Drafts hidden</Chip>
      {/if}

      <!-- Development filters -->
      {#if $typeFilter !== ProjectType.CONSULTING}
        {#if $tasksFilter}
          {#each $tasksFilter as tasksSectionFilter}
            <Chip
              color="green"
              onRemove={() =>
                ($tasksFilter = $tasksFilter.filter(
                  taskSection => taskSection !== tasksSectionFilter
                ))}>
              Task: {tasksSectionFilter}
            </Chip>
          {/each}
        {/if}

        {#if $technologiesFilter}
          {#each $technologiesFilter as technologyFilter}
            <Chip
              color="green"
              onRemove={() =>
                ($technologiesFilter = $technologiesFilter.filter(
                  technology => technology !== technologyFilter
                ))}>
              Technology: {technologyFilter}
            </Chip>
          {/each}
        {/if}
      {/if}

      <!-- Consulting filters -->
      {#if $typeFilter !== ProjectType.DEVELOPMENT}
        {#if $formatsFilter}
          {#each $formatsFilter as formatFilter}
            <Chip
              color="yellow"
              onRemove={() =>
                ($formatsFilter = $formatsFilter.filter(
                  format => format !== formatFilter
                ))}>
              Format: {formatFilter}
            </Chip>
          {/each}
        {/if}

        {#if $methodsFilter}
          {#each $methodsFilter as methodFilter}
            <Chip
              color="yellow"
              onRemove={() =>
                ($methodsFilter = $methodsFilter.filter(
                  method => method !== methodFilter
                ))}>
              Format: {methodFilter}
            </Chip>
          {/each}
        {/if}

        {#if $minValueInTimeFilter != null}
          <Chip color="yellow" onRemove={() => ($minValueInTimeFilter = null)}>
            Minimum value in Time: {$minValueInTimeFilter}&nbsp;h
          </Chip>
        {/if}

        {#if $maxValueInTimeFilter != null}
          <Chip color="yellow" onRemove={() => ($maxValueInTimeFilter = null)}>
            Maximum value in Time: {$maxValueInTimeFilter}&nbsp;h
          </Chip>
        {/if}

        {#if $minValueInPriceFilter != null}
          <Chip color="yellow" onRemove={() => ($minValueInPriceFilter = null)}>
            Minimum value in Price: {$minValueInPriceFilter}€
          </Chip>
        {/if}

        {#if $maxValueInPriceFilter != null}
          <Chip color="yellow" onRemove={() => ($maxValueInPriceFilter = null)}>
            Maximum value in Price: {$maxValueInPriceFilter}€
          </Chip>
        {/if}
      {/if}

      <Chip onClick={() => dispatch('resetFilters')}>Reset filters</Chip>
    </div>
  {/if}

  <p class="numberOfProjects">
    {numberOfProjectsFiltered} of {numberOfProjectsTotal} projects
  </p>
</div>
